import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { ReactSVG } from 'react-svg'
import { LangValue } from '../../../states/types/common'
import { format, dateManipulateDay, nowDate, diffNowToDate } from '../../../utils/date'
import { getZendeskToken } from '../../../states/apis/auth'
import getZendeskArticleList from '../../../states/apis/zendeskArticle'
import { ZendeskArticle } from '../../../states/types/zendeskArticle'
import { setErrors } from '../../../states/containers/common/errorsSlice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { openNewTab, setPath } from '../../../utils/url'
import { URL } from '../../constants/url'
import { sendLogout } from '../../../states/containers/auth/loginSlice'
import { resetState } from '../../../states/containers/common/commonPartSlice'
import Cookies from 'universal-cookie'
import { useAlert } from 'react-alert'
import ProfileImageModal from './ProfileImageModal'
import { resetMember } from '../../../states/containers/member/listSlice'
import { resetTeam } from '../../../states/containers/team/listSlice'
import { isAdministrator } from '../../../utils/role'

declare global {
  interface Window {
    zE?: (...args: any[]) => void
    zESettings? : ({ webWidget: { launcher: { chatLabel: { '*': string; }; }; }; })
  }
}

const Header: FC = () => {
  const dispatch = useDispatch()
  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()
  const history = useHistory()
  const cookies = new Cookies()
  const alert = useAlert()

  const {
    profile,
    commonPart,
    login,
  } = useSelector((state: RootState) => state)

  const [isOpenNotice, setIsOpenNotice] = useState<boolean>(false)
  const [isOpenPersonal, setIsOpenPersonal] = useState<boolean>(false)

  const [isDisplayProfileImageModal, setIsDisplayProfileImageModal] = useState(false)
  const [profileImageOrg, setProfileImageOrg] = useState<string | ArrayBuffer | null>(null)

  const alreadyPaidOrDuringTrial = useMemo(() => (
    commonPart.payment_finished || diffNowToDate(String(commonPart.trial_end_date)) >= 0
  ), [commonPart])

  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>

  const [release, SetRelease] = useState<ZendeskArticle[]>([])

  const handleListClick = (url: string) => {
    if (url.includes("qasee.zendesk.com")) {
      getZendeskToken()
        .then((result) => {
          openNewTab(`${result.url}&return_to=${url}`, 'post')
        })
        .catch((error) => {
          dispatch(setErrors(error))
        })
    } else {
      window.open(url, '_blank')
    }
  }

  const handleAllListClick = () => {
    getZendeskToken()
      .then((result) => {
        const url = "https://qasee.zendesk.com/hc/ja/sections/4407439741849"
        openNewTab(`${result.url}&return_to=${url}`, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  const selectProfileImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setProfileImageOrg(reader.result)
    })
    reader.readAsDataURL(e.target.files[0])
    setIsDisplayProfileImageModal(true)
    setIsOpenPersonal(false)
  }

  const handleProfileImage = () => {
    inputRef.current.click()
  }

  const closeProfileImageModal = () => {
    setProfileImageOrg(null)
    setIsDisplayProfileImageModal(false)
  }

  const closeDropdown = () => {
    setIsOpenNotice(false)
    setIsOpenPersonal(false)
  }

  const handleLogout = () => {
    cookies.remove('client_update_modal_showed')
    if (document.getElementsByClassName('modal-backdrop')[0]) {
      document.getElementsByClassName('modal-backdrop')[0].remove()
    }

    alert.success(t('authorization.logout.complete'), {
      timeout: 3000,
    })

    dispatch(sendLogout(() => {
      history.push('/')
      dispatch(resetState())
      dispatch(resetMember())
      dispatch(resetTeam())
      dispatch({ type: 'logout' })
    }))
  }

  const moveProfile = () => {
    setIsOpenPersonal(false)
    history.push(setPath(URL.SETTING_PROFILE.path, language))
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isOpenNotice || isOpenPersonal) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isOpenNotice, isOpenPersonal])

  useEffect(() => {
    if (login.accessToken) {
      getZendeskArticleList()
        .then((result) => {
          SetRelease(result)
        })
        .catch((error) => {
          dispatch(setErrors(error))
        })
    }
  }, [login.accessToken])

  return (
    <>
      <section className="header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
          }}
        >
          <div className="logo">
            <img src="/static/images/logo.png" alt="" />
          </div>
          {
            !commonPart.isLoading && !alreadyPaidOrDuringTrial && (
              <div
                style={{
                  backgroundColor: "#FF0000",
                  color: "#FFFFFF",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                トライアル期間が終了しました。新たにデータは収集されず、アラート機能は制限されます。
                <a
                  href="https://timerex.net/s/Qasee_sales/ff84a870"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "#FFFFFF",
                    textDecoration: "underline",
                  }}
                >
                  トライアル後のご相談はこちらから
                </a>
              </div>
            )
          }
        </div>
        <div className="tools">
          {
            alreadyPaidOrDuringTrial || isAdministrator(login.role) ? (
              <>
                <div className="notice">
                  <ReactSVG
                    src="/static/images/icon/icon-alert.svg"
                    className="icon"
                    onClick={() => setIsOpenNotice(true)}
                  />
                  <div
                    style={{
                      fontSize: "8px",
                      textAlign: "center",
                      marginTop: "2px",
                    }}
                  >
                    お知らせ
                  </div>
                  {
                    release.filter((v) => format(v.release_date) > dateManipulateDay(nowDate(), -7)).length ? (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          backgroundColor: "#FF0000",
                          borderRadius: "50%",
                          position: "absolute",
                          top: 0,
                          right: 0,
                        }}
                      />
                    ) : (
                      <></>
                    )
                  }
                  {
                    isOpenNotice && (
                      <ul className="dropdown">
                        <li className="title">
                          {t('notice.caption')}
                        </li>
                        {
                          !!release.length && release.map((value) => (
                            <li className="item" key={`notice_${value.release_date}`} onClick={() => handleListClick(value.url)}>
                              <div>
                                {value.title}
                              </div>
                              <FontAwesomeIcon
                                icon={['fas', 'external-link-alt']}
                                pull="right"
                                className="external"
                              />
                              {
                                format(value.release_date) > dateManipulateDay(nowDate(), -7) && (
                                  <div className="new">new</div>
                                )
                              }
                            </li>
                          ))
                        }
                        <li className="item center" key="notice_99" onClick={() => handleAllListClick()}>
                          <div>
                            {t('layouts.main_header.notice.view_all')}
                          </div>
                          <FontAwesomeIcon
                            icon={['fas', 'external-link-alt']}
                            pull="right"
                            className="external"
                          />
                        </li>
                      </ul>
                    )
                  }
                </div>
                <div className="profile">
                  <div
                    className="display"
                    onClick={() => setIsOpenPersonal(true)}
                    role="presentation"
                  >
                    {
                      !profile.isLoading && (
                        profile.profile_image_url ? (
                          <img alt="" src={profile.profile_image_url} className="icon" />
                        ) : (
                          <img alt="" src={`/static/images/profile/${(commonPart.current_member?.id || 0) % 12}.jpeg`} className="icon" />
                        )
                      )
                    }
                    <span className="name">{commonPart.current_member?.name}</span>
                  </div>
                  {
                    isOpenPersonal && (
                      <ul className="dropdown">
                        <input type="file" accept="image/*" onChange={(e) => selectProfileImage(e)} ref={inputRef} className="hidden" />
                        <li className="title">
                          <div className="set_image" role="none" onClick={() => handleProfileImage()} title={t('setting.profile.button.create')}>
                            <div className="hover_filter">
                              <FontAwesomeIcon
                                icon={['fas', 'camera']}
                              />
                            </div>
                            {
                              !profile.isLoading && (
                                profile.profile_image_url ? (
                                  <img alt="" src={profile.profile_image_url} className="icon" />
                                ) : (
                                  <img alt="" src={`/static/images/profile/${(commonPart.current_member?.id || 0) % 12}.jpeg`} className="icon" />
                                )
                              )
                            }
                          </div>
                          <p>{commonPart.current_member?.name}</p>
                        </li>
                        <li className="item" onClick={moveProfile}>
                          <ReactSVG
                            src="/static/images/icon/icon-profile.svg"
                            className="icon"
                          />
                          {t('layouts.main_header.user.profile')}
                        </li>
                        <li className="item" onClick={handleLogout}>
                          <ReactSVG
                            src="/static/images/icon/icon-signout.svg"
                            className="icon"
                          />
                          {t('layouts.main_header.user.logout')}
                        </li>
                      </ul>
                    )
                  }
                </div>
              </>
            ) : (
              <>
                <div className="profile">
                  <button
                    type="button"
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                    onClick={handleLogout}
                  >
                    ログアウト
                  </button>
                </div>
              </>
            )
          }
        </div>
        {
          (isOpenNotice || isOpenPersonal) && (
            <div
              className="close_screen gray"
              onClick={closeDropdown}
              role="presentation"
            />
          )
        }
      </section>
      <ProfileImageModal
        isOpen={isDisplayProfileImageModal}
        closeCallback={closeProfileImageModal}
        profileImageProp={profileImageOrg}
      />
    </>
  )
}

export default Header
